import type { NotificationDto } from "api/types";

/** A translation key plus the values it interpolates, for the caller to hand to `t`. */
export type NotificationCopy = {
	key: string;
	values: Record<string, string>;
};

/**
 * The sentence that describes a notification.
 *
 * Every known type is matched explicitly and anything unrecognised falls to a neutral line. That
 * distinction is the whole point: this used to end by returning the comment sentence, so a type
 * the chain did not recognise announced itself as a comment — an accepted invitation read as
 * "commented on your tree". A notification that says nothing useful is a small problem; one that
 * says something untrue is not.
 *
 * The bell and the notifications page both render this, which is why it lives here rather than
 * once in each of them.
 */
export const notificationCopy = (notification: NotificationDto): NotificationCopy => {
	const values: Record<string, string> = { name: notification.actorName, tree: notification.treeTitle };

	switch (notification.type) {
		case "TreeComment":
			return { key: "view.notifTreeComment", values };
		case "CommentReply":
			return { key: "view.notifCommentReply", values };
		case "TreeLike":
			return { key: "view.notifTreeLike", values };
		case "TreeShared":
			return { key: "view.notifTreeShared", values };
		case "TreeStar":
			return { key: "view.notifTreeStar", values };
		case "TreeShareAccepted":
			return { key: "view.notifTreeShareAccepted", values };
		case "TreeRoleChanged":
			// The API stamps the new role onto the row. Without one the sentence has nothing to
			// name, so it takes the neutral line rather than trailing off mid-clause.
			return notification.role
				? { key: "view.notifTreeRoleChanged", values: { ...values, role: notification.role } }
				: { key: "view.notifGeneric", values };
		default:
			return { key: "view.notifGeneric", values };
	}
};
